import React, {useState, useEffect} from "react";
import {Button, Card, Form, Input, message, Statistic, Typography, Space} from "antd";
import {LockOutlined, RedoOutlined} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Countdown } = Statistic;

const OtpVerificationStep = ({ onSubmit, loading, lastDigit, onResend }) => {
    const [form] = Form.useForm();
    const [deadline, setDeadline] = useState(Date.now() + 1000 * 60 * 3);

    const [canResend, setCanResend] = useState(false);
    const [resending, setResending] = useState(false);
    const [isOtpComplete, setIsOtpComplete] = useState(false);

    // Use Form.useWatch to monitor the 'otp' field
    const otpValue = Form.useWatch('otp', form);

    useEffect(() => {
        // Update the completion state whenever the OTP value changes
        setIsOtpComplete(otpValue && otpValue.length === 6);
    }, [otpValue]);

    const onFinishCountdown = () => {
        setCanResend(true);
    };

    const handleResendClick = async () => {
        setResending(true);
        const success = await onResend();

        if (success) {
            setDeadline(Date.now() + 1000 * 60 * 3);
            setCanResend(false);
            message.success('New code sent successfully');
        }
        setResending(false);
    };

    return (
        <Card
            variant={"borderless"}
            style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', borderRadius: '8px' }}
        >
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                <div style={{ margin: '0 auto 1rem', width: '3rem', height: '3rem', backgroundColor: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <LockOutlined style={{ fontSize: '1.5rem', color: '#2563eb' }} />
                </div>
                <Title level={3} style={{ marginBottom: '0.5rem', color: '#1f2937' }}>Verify otp</Title>
                <Text type="secondary" style={{ padding: '0 1rem', display: 'block' }}>
                    Enter the 6-digit code sent to your registered number ending in
                    <span style={{ fontWeight: 600, color: '#374151' }}> {lastDigit}</span>.
                </Text>
            </div>

            <Form form={form} layout="vertical" onFinish={onSubmit} size="large">
                <Form.Item
                    name="otp"
                    rules={[
                        { required: true, message: 'Please enter the code' },
                        { len: 6, message: 'Code must be 6 digits' }
                    ]}
                    style={{ textAlign: 'center' }}
                >
                    <Input.OTP length={6} />
                </Form.Item>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                    <Text type="secondary">Code expires in:</Text>

                    {canResend ? (
                        <Button
                            type="link"
                            onClick={handleResendClick}
                            loading={resending}
                            style={{ padding: 0, height: 'auto' }}
                        >
                            <Space size={4}>
                                <RedoOutlined style={{ fontSize: '0.75rem' }} />
                                Resend Code
                            </Space>
                        </Button>
                    ) : (
                        <Countdown
                            value={deadline}
                            format="mm:ss"
                            onFinish={onFinishCountdown}
                            valueStyle={{ fontSize: '0.875rem', color: '#64748b' }}
                        />
                    )}
                </div>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                        size="large"
                        style={{ height: '2.75rem', fontWeight: 500 }}
                        disabled={canResend || !isOtpComplete}
                    >
                        Verify Identity
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default OtpVerificationStep;